// Weekly Management Summary (cadence="week") — one per project per WSR week,
// built/refreshed straight from that week's Weekly Site Report. Auto content
// is prepared by Director / Administrator / assigned Consultant; any one
// nominated reviewer (or internal staff) can add management comments and
// finalise. Legacy Fortnightly Summaries (cadence="fortnight") keep their
// original Sign & lock / Send workflow below, read via the same route.
// Shares the WSR's colour/typography via SectionCard/Field/TextInput/Pill.
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Save, PenLine, Send, Loader2, CheckCircle2, ShieldCheck, MessageSquare, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SignaturePad from "@/components/SignaturePad";
import { fmtDate, fmtDateTime, getIn, setIn } from "./wsrConstants";
import { SectionCard, Field, TextInput, Pill } from "./WsrKit";
import WeeklyManagementSummaryView from "./WeeklyManagementSummaryView";

const CAN_EDIT_ROLES = ["director", "administrator", "consultant"];
const STATUS_META = {
    draft: { label: "Draft — not yet released", bg: "#FDF0DC", fg: "#B5720A" },
    released: { label: "Released", bg: "#EAF1FB", fg: "#2A5DB0" },
    final: { label: "Final", bg: "#E4F1E6", fg: "#2E7D32" },
    signed: { label: "Signed — ready to send", bg: "#EAF1FB", fg: "#2A5DB0" },
    sent: { label: "Sent", bg: "#E4F1E6", fg: "#2E7D32" },
};
const TIME_TONE = { "On plan": { bg: "#E4F1E6", fg: "#2E7D32" }, Watch: { bg: "#FDF0DC", fg: "#B5720A" }, Delay: { bg: "#FBE3E1", fg: "#B3261E" } };

function SendDialog({ reportId, label, onClose, onSent }) {
    const [recipients, setRecipients] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        api.get(`/fortnightly-reports/${reportId}/recipients`)
            .then((r) => setRecipients(r.data))
            .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)))
            .finally(() => setLoading(false));
    }, [reportId]);

    const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    const send = async () => {
        if (!selected.length) { toast.error("Select at least one recipient"); return; }
        setSending(true);
        try {
            const { data } = await api.post(`/fortnightly-reports/${reportId}/send`, { recipient_user_ids: selected });
            toast.success(`${label} shared`);
            onSent(data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSending(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
            <div data-testid="fn-send-dialog" className="bg-white rounded-2xl border border-[#E5E0DA] p-5 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-[15px] font-bold text-[#1F1F1F]">Share {label}</h3>
                <p className="text-[12.5px] text-[#6F6A64]">Choose which PR Client portal users should receive this — this is optional, not a required step.</p>
                {loading ? (
                    <div className="py-6 text-center text-[#9A968F]"><Loader2 size={18} className="animate-spin inline" /></div>
                ) : recipients.length === 0 ? (
                    <p className="text-[12.5px] text-[#9A968F]">No client portal users found for this project's client.</p>
                ) : (
                    <div className="space-y-1.5 max-h-64 overflow-y-auto">
                        {recipients.map((r) => (
                            <label key={r.id} data-testid={`fn-send-recipient-${r.id}`} className="flex items-center gap-2.5 text-[13px] text-[#1F1F1F] rounded-lg border border-[#E5E0DA] p-2.5 cursor-pointer hover:bg-[#F5F3F0]">
                                <input type="checkbox" className="w-4 h-4 accent-[#C75A1B]" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} />
                                <div>
                                    <div className="font-semibold">{r.name}</div>
                                    <div className="text-[11px] text-[#9A968F]">{r.email} · {r.role}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                )}
                <div className="flex items-center justify-end gap-2 pt-1">
                    <button data-testid="fn-send-cancel" onClick={onClose} className="rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold text-[#6F6A64] hover:bg-[#F5F3F0]">Cancel</button>
                    <button data-testid="fn-send-confirm" onClick={send} disabled={sending || !selected.length}
                            className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[12.5px] font-semibold text-white bg-[#C75A1B] hover:bg-[#9F4212] disabled:opacity-50">
                        {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Share
                    </button>
                </div>
            </div>
        </div>
    );
}

function FinalizeDialog({ reportId, defaultName, defaultPosition, onClose, onFinalized }) {
    const [authorName, setAuthorName] = useState(defaultName || "");
    const [position, setPosition] = useState(defaultPosition || "");
    const [signature, setSignature] = useState(null);
    const [busy, setBusy] = useState(false);

    const finalize = async () => {
        if (!authorName.trim()) { toast.error("Enter your name"); return; }
        if (!signature) { toast.error("Please sign before finalising"); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/fortnightly-reports/${reportId}/finalize`, { author_name: authorName, position, signature });
            toast.success("Summary finalised");
            onFinalized(data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
            <div data-testid="ms-finalize-dialog" className="bg-white rounded-2xl border border-[#E5E0DA] p-5 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-[15px] font-bold text-[#1F1F1F]">Finalise Weekly Management Summary</h3>
                <p className="text-[12.5px] text-[#6F6A64]">Once finalised by any one reviewer, it's done for everyone — no further reminders.</p>
                <Field label="Name"><TextInput value={authorName} onChange={setAuthorName} testId="ms-finalize-name" /></Field>
                <Field label="Position"><TextInput value={position} onChange={setPosition} testId="ms-finalize-position" /></Field>
                <Field label="Signature"><SignaturePad value={signature} onChange={setSignature} /></Field>
                <div className="flex items-center justify-end gap-2 pt-1">
                    <button data-testid="ms-finalize-cancel" onClick={onClose} className="rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold text-[#6F6A64] hover:bg-[#F5F3F0]">Cancel</button>
                    <button data-testid="ms-finalize-confirm" onClick={finalize} disabled={busy}
                            className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[12.5px] font-semibold text-white bg-[#C75A1B] hover:bg-[#9F4212] disabled:opacity-50">
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} Finalise
                    </button>
                </div>
            </div>
        </div>
    );
}

function CommentsPanel({ report, onAdded }) {
    const [comment, setComment] = useState("");
    const [posting, setPosting] = useState(false);

    const addComment = async () => {
        if (!comment.trim()) { toast.error("Write a comment first"); return; }
        setPosting(true);
        try {
            const { data } = await api.post(`/fortnightly-reports/${report.id}/comments`, { comment });
            setComment("");
            onAdded(data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setPosting(false); }
    };

    return (
        <SectionCard title="Management comments">
            {(report.management_comments || []).length === 0 && (
                <p className="text-[12.5px] text-[#9A968F]">No comments yet.</p>
            )}
            <div className="space-y-2.5">
                {(report.management_comments || []).map((c) => (
                    <div key={c.id} data-testid={`ms-comment-${c.id}`} className="rounded-xl border border-[#E5E0DA] bg-white p-3">
                        <div className="text-[12.5px] text-[#1F1F1F]">{c.comment}</div>
                        <div className="text-[11px] text-[#9A968F] mt-1">{c.author_name} · {fmtDateTime(c.at)}</div>
                    </div>
                ))}
            </div>
            {report.can_finalize && (
                <div className="flex items-start gap-2 pt-1">
                    <textarea data-testid="ms-comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
                              placeholder="Add a management comment…" rows={2}
                              className="flex-1 rounded-xl border border-[#E5E0DA] p-2.5 text-[13px]" />
                    <button data-testid="ms-comment-submit" onClick={addComment} disabled={posting}
                            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[12.5px] font-semibold text-white bg-[#C75A1B] hover:bg-[#9F4212] disabled:opacity-50">
                        {posting ? <Loader2 size={13} className="animate-spin" /> : <MessageSquare size={13} />} Add
                    </button>
                </div>
            )}
        </SectionCard>
    );
}

export default function FortnightlyReportEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [report, setReport] = useState(null);
    const [content, setContent] = useState(null);
    const [saving, setSaving] = useState(false);
    const [signing, setSigning] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [sendOpen, setSendOpen] = useState(false);
    const [finalizeOpen, setFinalizeOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);

    useEffect(() => {
        api.get(`/fortnightly-reports/${id}`)
            .then((r) => { setReport(r.data); setContent(r.data.content); setDirty(false); })
            .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
    }, [id]);

    useEffect(() => {
        if (!report || !content || report.cadence === "week") return;
        const g2 = (p, fb) => getIn(content, p, fb);
        if (g2("sign_off.author_name")) return;
        let next = setIn(content, "sign_off.author_name", user?.name || "");
        next = setIn(next, "sign_off.position", user?.title || "");
        setContent(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [report?.id]);

    if (!report || !content) return <div className="font-serif-italic text-[#9A968F] py-12 text-center">Loading report…</div>;

    const isWeekly = report.cadence === "week";
    const canEdit = CAN_EDIT_ROLES.includes(user?.role) && (isWeekly ? report.status !== "final" : report.status === "draft");
    const label = isWeekly ? "Weekly Management Summary" : "Fortnightly Senior Management Summary";
    const g = (path, fb) => getIn(content, path, fb);
    const set = (path, value) => { setContent((c) => setIn(c, path, value)); setDirty(true); };

    const save = async (silent) => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/fortnightly-reports/${id}`, { content });
            setReport((r) => ({ ...r, ...data })); setContent(data.content); setDirty(false);
            if (!silent) toast.success("Draft saved");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    const signOff = async () => {
        if (!g("sign_off.signature")) { toast.error("Please sign before locking the report"); return; }
        setSigning(true);
        try {
            if (dirty) await save(true);
            const { data } = await api.post(`/fortnightly-reports/${id}/sign-off`, {
                author_name: g("sign_off.author_name"), position: g("sign_off.position"), signature: g("sign_off.signature"),
            });
            setReport((r) => ({ ...r, ...data })); setContent(data.content); setDirty(false);
            toast.success("Report signed — ready to send");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSigning(false); }
    };

    const downloadPdf = async () => {
        try {
            const res = await api.get(`/fortnightly-reports/${id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = url; a.download = `${report.report_number}.pdf`; document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error("Could not generate the PDF"); }
    };

    const goBack = () => {
        if (dirty && !window.confirm("You have unsaved changes. Leave without saving?")) return;
        navigate("/app/weekly-reports");
    };

    const statusMeta = STATUS_META[report.status] || STATUS_META.draft;
    const timeStatus = g("time.status") || "On plan";
    const risk = g("risk", {});
    const info = g("information", {});
    const safety = g("safety", {});

    return (
        <div data-testid="fortnightly-report-editor" className="space-y-6 max-w-5xl">
            <div className="flex items-center justify-between gap-3 flex-wrap sticky top-0 z-10 bg-[#F5F3F0] py-2 -mt-2">
                <button data-testid="fn-back" onClick={goBack} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#6F6A64] hover:text-[#1F1F1F]">
                    <ArrowLeft size={15} /> Weekly Site Reports
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                    {report.late && <span data-testid="fn-late-pill" className="px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: "#FBE6E6", color: "#B0262B" }}>Late</span>}
                    <span data-testid="fn-status-pill" className="px-2.5 py-1 rounded-full text-[11px] font-semibold" style={{ background: statusMeta.bg, color: statusMeta.fg }}>{statusMeta.label}</span>
                    <button data-testid="fn-export-pdf" onClick={downloadPdf}
                            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold border border-[#E5E0DA] bg-white hover:bg-[#F5F3F0]">
                        <Download size={13} /> Export PDF
                    </button>
                    {!isWeekly && canEdit && (
                        <button data-testid="fn-save" onClick={() => save(false)} disabled={saving || !dirty}
                                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold border border-[#E5E0DA] bg-white hover:bg-[#F5F3F0] disabled:opacity-50">
                            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save draft
                        </button>
                    )}
                    {!isWeekly && canEdit && (
                        <button data-testid="fn-sign" onClick={signOff} disabled={signing}
                                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[12.5px] font-semibold text-white bg-[#C75A1B] hover:bg-[#9F4212] disabled:opacity-50">
                            {signing ? <Loader2 size={13} className="animate-spin" /> : <PenLine size={13} />} Sign & lock
                        </button>
                    )}
                    {!isWeekly && report.status === "signed" && CAN_EDIT_ROLES.includes(user?.role) && (
                        <button data-testid="fn-send-now" onClick={() => setSendOpen(true)}
                                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[12.5px] font-semibold text-white bg-[#C75A1B] hover:bg-[#9F4212]">
                            <Send size={13} /> Send now
                        </button>
                    )}
                    {isWeekly && ["released", "final"].includes(report.status) && (
                        <button data-testid="fn-send-now" onClick={() => setSendOpen(true)}
                                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold border border-[#E5E0DA] bg-white hover:bg-[#F5F3F0]">
                            <Send size={13} /> Share
                        </button>
                    )}
                    {isWeekly && report.status !== "final" && report.can_finalize && (
                        <button data-testid="ms-edit-toggle" onClick={() => setEditMode((v) => !v)}
                                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold border border-[#E5E0DA] bg-white hover:bg-[#F5F3F0]">
                            <PenLine size={13} /> {editMode ? "Done editing" : "Edit"}
                        </button>
                    )}
                    {isWeekly && editMode && report.status === "released" && report.can_finalize && (
                        <button data-testid="ms-finalize-btn" onClick={() => setFinalizeOpen(true)}
                                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[12.5px] font-semibold text-white bg-[#C75A1B] hover:bg-[#9F4212]">
                            <ShieldCheck size={13} /> Finalise
                        </button>
                    )}
                </div>
            </div>

            <div>
                <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase" style={{ borderBottom: "2px solid #C75A1B", color: "#6F6A64" }}>
                    {report.report_number} · {report.client_name}
                </span>
                <h2 className="mt-3 text-2xl font-bold tracking-tight text-[#1F1F1F]">{report.project_name}</h2>
                <p className="text-[13px] mt-1 text-[#6F6A64]">
                    {isWeekly ? `Week commencing ${fmtDate(report.week_start)}` : `Fortnight: ${fmtDate(report.fortnight_start)} – ${fmtDate(report.fortnight_end)}`}
                </p>
            </div>

            {isWeekly && report.source_wsr_returned && (
                <div data-testid="ms-returned-banner" className="rounded-xl border p-3.5 flex gap-2.5" style={{ background: "#FDF0DC", borderColor: "#F0D9A8" }}>
                    <RotateCcw size={18} className="text-[#B5720A] shrink-0 mt-0.5" />
                    <div className="text-[13px] font-bold text-[#B5720A]">Site report returned – awaiting resubmission. This summary will update automatically once it's resubmitted.</div>
                </div>
            )}

            {report.status === "final" && (
                <div data-testid="ms-final-banner" className="rounded-xl border p-3.5 flex gap-2.5" style={{ background: "#E4F1E6", borderColor: "#BFDFC4" }}>
                    <CheckCircle2 size={18} className="text-[#2E7D32] shrink-0 mt-0.5" />
                    <div>
                        <div className="text-[13px] font-bold text-[#2E7D32]">Finalised {fmtDateTime(report.finalized_at)} by {report.finalized_by_name}</div>
                        {report.final_sign_off?.position && <p className="text-[12.5px] text-[#1F1F1F] mt-1">{report.final_sign_off.position}</p>}
                    </div>
                </div>
            )}

            {report.status === "sent" && (
                <div data-testid="fn-sent-banner" className="rounded-xl border p-3.5 flex gap-2.5" style={{ background: "#E4F1E6", borderColor: "#BFDFC4" }}>
                    <CheckCircle2 size={18} className="text-[#2E7D32] shrink-0 mt-0.5" />
                    <div>
                        <div className="text-[13px] font-bold text-[#2E7D32]">Sent {fmtDateTime(report.sent_at)} by {report.sent_by_name}</div>
                        <p className="text-[12.5px] text-[#1F1F1F] mt-1">To: {(report.sent_to || []).map((r) => r.name || r.email).join(", ")}</p>
                    </div>
                </div>
            )}

            {isWeekly && report.sent_to?.length > 0 && report.status !== "sent" && (
                <div data-testid="ms-shared-banner" className="rounded-xl border p-3 text-[12.5px] text-[#1F1F1F]" style={{ background: "#F5F3F0", borderColor: "#E5E0DA" }}>
                    Shared {fmtDateTime(report.sent_at)} by {report.sent_by_name} with {(report.sent_to || []).map((r) => r.name || r.email).join(", ")}
                </div>
            )}

            {isWeekly ? (
                <>
                    <WeeklyManagementSummaryView report={report} content={content} editMode={editMode}
                        onUpdated={(data) => { setReport((r) => ({ ...r, ...data })); setContent(data.content); }} />
                    {editMode && <CommentsPanel report={report} onAdded={(data) => setReport(data)} />}
                </>
            ) : (
                <>
                    <SectionCard title="Report details">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <Field label="Contract"><TextInput value={g("header.project_name")} disabled onChange={() => {}} testId="fn-header-project" /></Field>
                            <Field label="Site Manager"><TextInput value={g("header.site_manager_name")} disabled onChange={() => {}} testId="fn-header-sm" /></Field>
                            <Field label="Client / Employer"><TextInput value={g("header.client_employer")} disabled onChange={() => {}} testId="fn-header-client" /></Field>
                            <Field label="Client Representative"><TextInput value={g("header.client_rep")} disabled onChange={() => {}} testId="fn-header-rep" /></Field>
                            <Field label="Distribution"><TextInput value={g("header.distribution")} disabled onChange={() => {}} testId="fn-header-dist" /></Field>
                            <Field label="Report Number"><TextInput value={report.report_number} disabled onChange={() => {}} testId="fn-header-number" /></Field>
                        </div>
                    </SectionCard>

                    <SectionCard title="Time">
                        <div className="flex items-center gap-2">
                            {["On plan", "Watch", "Delay"].map((opt) => (
                                <Pill key={opt} active={timeStatus === opt} onClick={() => canEdit && set("time.status", opt)} disabled={!canEdit} testId={`fn-time-${opt.replace(" ", "-").toLowerCase()}`}>{opt}</Pill>
                            ))}
                        </div>
                        <Field label="Current programme position">
                            <TextInput textarea value={g("time.current_position")} disabled={!canEdit} onChange={(v) => set("time.current_position", v)} testId="fn-time-position" />
                        </Field>
                    </SectionCard>

                    <SectionCard title="Safety">
                        <div className="flex items-center gap-4 flex-wrap text-[13px]">
                            <span className="px-2.5 py-1 rounded-full text-[11.5px] font-semibold" style={{ background: safety.no_incidents ? "#E4F1E6" : "#FBE3E1", color: safety.no_incidents ? "#2E7D32" : "#B3261E" }}>
                                {safety.no_incidents ? "No incidents" : `${safety.incident_count || 0} incident(s) recorded`}
                            </span>
                            <span className="text-[#6F6A64]">{safety.actions_open || 0} open action(s) on this project</span>
                        </div>
                        <Field label="Note (optional)">
                            <TextInput textarea value={g("safety.note")} disabled={!canEdit} onChange={(v) => set("safety.note", v)} testId="fn-safety-note" />
                        </Field>
                    </SectionCard>

                    <SectionCard title="Information">
                        <div className="flex items-center gap-2">
                            <Pill active={!!info.required} onClick={() => canEdit && set("information.required", !info.required)} disabled={!canEdit} testId="fn-info-required">Required</Pill>
                            <Pill active={!!info.received} onClick={() => canEdit && set("information.received", !info.received)} disabled={!canEdit} testId="fn-info-received">Received</Pill>
                            <span className="text-[12.5px] text-[#6F6A64]">{info.outstanding_count || 0} outstanding approval(s)/RFI(s)</span>
                        </div>
                        {(info.items || []).length > 0 && (
                            <div className="rounded-xl border border-[#E5E0DA] divide-y divide-[#F0ECE5] bg-white">
                                {info.items.map((it, i) => (
                                    <div key={i} data-testid={`fn-info-item-${i}`} className="px-3 py-2 text-[12.5px] flex flex-wrap gap-2 justify-between">
                                        <span className="font-semibold text-[#1F1F1F]">{it.title}</span>
                                        <span className="text-[#6F6A64]">Due {it.due || "—"} · {it.status}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </SectionCard>

                    <SectionCard title="Risk">
                        {[["materials", "Materials"], ["labour", "Labour"], ["weather", "Weather constraints"]].map(([key, label]) => {
                            const cat = risk[key] || {};
                            return (
                                <div key={key} className="space-y-1.5 pb-2 border-b border-[#F0ECE5] last:border-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[13px] font-semibold text-[#1F1F1F] w-36">{label}</span>
                                        <Pill active={!!cat.flagged} onClick={() => canEdit && set(`risk.${key}.flagged`, !cat.flagged)} disabled={!canEdit} testId={`fn-risk-${key}-flag`}>
                                            {cat.flagged ? "Flagged" : "Not flagged"}
                                        </Pill>
                                    </div>
                                    {cat.flagged && (
                                        <TextInput value={cat.note} disabled={!canEdit} onChange={(v) => set(`risk.${key}.note`, v)}
                                                   placeholder="Short note on this risk" testId={`fn-risk-${key}-note`} />
                                    )}
                                </div>
                            );
                        })}
                        {(risk.reference_risks || []).length > 0 && (
                            <div className="pt-2">
                                <div className="text-[11px] font-semibold uppercase tracking-[.05em] text-[#8B867E] mb-1.5">Open risks referenced this fortnight</div>
                                <div className="rounded-xl border border-[#E5E0DA] divide-y divide-[#F0ECE5] bg-white">
                                    {risk.reference_risks.map((r, i) => (
                                        <div key={i} data-testid={`fn-risk-ref-${i}`} className="px-3 py-2 text-[12.5px] flex flex-wrap gap-2 justify-between">
                                            <span className="font-mono text-[#8B867E]">{r.ref}</span>
                                            <span className="font-semibold text-[#1F1F1F] flex-1 min-w-[160px]">{r.risk}</span>
                                            <span className="text-[#6F6A64]">{r.impact} · {r.status}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </SectionCard>

                    <SectionCard title="Topic Summary">
                        {[["time", "Time"], ["safety", "Safety"], ["information", "Information"], ["risk", "Risk"]].map(([key, label]) => (
                            <Field key={key} label={label}>
                                <TextInput value={g(`topic_summary.${key}`)} disabled={!canEdit} onChange={(v) => set(`topic_summary.${key}`, v)} testId={`fn-summary-${key}`} />
                            </Field>
                        ))}
                    </SectionCard>

                    <SectionCard title="Sign-Off">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <Field label="Prepared by"><TextInput value={g("sign_off.author_name")} disabled={!canEdit} onChange={(v) => set("sign_off.author_name", v)} testId="fn-signoff-author" /></Field>
                            <Field label="Position"><TextInput value={g("sign_off.position")} disabled={!canEdit} onChange={(v) => set("sign_off.position", v)} testId="fn-signoff-position" /></Field>
                        </div>
                        <Field label="Signature">
                            <SignaturePad value={g("sign_off.signature")} disabled={!canEdit} onChange={(v) => set("sign_off.signature", v)} />
                        </Field>
                        {report.status !== "draft" && (
                            <div className="text-[12.5px] text-[#6F6A64]">Signed by {report.signed_by_name} on {fmtDateTime(report.signed_at)}</div>
                        )}
                    </SectionCard>
                </>
            )}

            {sendOpen && (
                <SendDialog reportId={id} label={label} onClose={() => setSendOpen(false)}
                            onSent={(data) => { setReport((r) => ({ ...r, ...data })); setContent(data.content); setSendOpen(false); }} />
            )}
            {finalizeOpen && (
                <FinalizeDialog reportId={id} defaultName={user?.name} defaultPosition={user?.title}
                                onClose={() => setFinalizeOpen(false)}
                                onFinalized={(data) => { setReport((r) => ({ ...r, ...data })); setFinalizeOpen(false); }} />
            )}
        </div>
    );
}
